import { isFunction, isObject, sortBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { exhibitorsFilter } from '../../filters/exhibitorsFilters';
import { useSponsors } from './useSponsors';
import { useEvent } from '../eventHooks';
import { useExhibitorsTagCategories } from './useExhibitorsTagCategories';
import { sanitizeExhibitorsFilter } from '../../misc/sanitizeExhibitorsFilter';
import { createDirectoryFilterFromFilterParams } from '../../filters/directory/createDirectoryFilterFromFilterParams/createDirectoryFilterFromFilterParams';

/**
 *
 * @param {Number}          eventId         Event identification
 * @param {function|object} options.filter  Filter to apply to exhibitors
 * @param {boolean}         options.filter.favorites    Only return marked as favorites
 * @param {string}          options.filter.text         Text filter (applied to name and description)
 * @param {object}          options.filter.tags         Tags filter (check reducer)
 * @param {function|array}  options.orderBy SortBy iteratee
 *
 * @returns {array} List of exhibitors
 */
export const useExhibitors = (eventId, options = {}) => {
    const { filter, orderBy } = options,
        all = useSelector((state) => state.events[eventId].exhibitors.all);

    return useMemo(() => {
        let exhibitors = all;

        if (filter) {
            if (isFunction(filter)) exhibitors = exhibitors.filter(filter);
            else if (isObject(filter)) exhibitors = exhibitors.filter(exhibitorsFilter.bind(this, filter));
        }
        if (sortBy) exhibitors = sortBy(exhibitors, orderBy);

        return exhibitors;
    }, [all, filter, orderBy]);
};

export const useExhibitorsRecommendedFilter = (eventId) => useSelector((state) => state.events[eventId].exhibitors.recommendedFilter);

export const useExhibitorsFilter = (eventId) => {
    const filter = useSelector((state) => state.events[eventId].exhibitors.filter),
        availableTags = useExhibitorsTagCategories(eventId);

    return useMemo(() => sanitizeExhibitorsFilter(filter, availableTags), [filter, availableTags]);
};

/**
 * Shows all the entries to show on the exhibitors list screen (includes sponsors)
 *
 * @param {Number} eventId
 * @param {object} options
 */
export const useExhibitorsList = (eventId, options = {}) => {
    const event = useEvent(eventId),
        repeatSponsors = event.repeat_sponsors_on_exhibitors_list,
        defaultFilter = useExhibitorsFilter(eventId),
        filter = options.filter ?? defaultFilter,
        filterFunction = isObject(filter) ? createDirectoryFilterFromFilterParams(filter) : filter,
        filteredExhibitors = useExhibitors(eventId, { filter: filterFunction }),
        sponsors = useSponsors(eventId, { filter: filterFunction });

    return useMemo(
        () =>
            sortBy(
                [...filteredExhibitors, ...(repeatSponsors ? Object.values(sponsors).filter((item) => item.is_sponsor && !item.is_exhibitor && !item.parent_id) : [])],
                ['position', (exhibitor) => exhibitor.name.toLowerCase()]
            ),
        [repeatSponsors, filteredExhibitors, sponsors]
    );
};
